import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { characters, paramsAdd, paramsEdit, sOption, assistantsApp, unitedStates, userCitizenshipCountryField, documentOperatorField, documentNationalityField, documentLastNationalityField, documentResidenceField, userAmericanCitizenField, bankReferencesField } from '../constants/form';
import { envSetting } from '../constants/envSetting';
import { changeCitizenByOperator, fieldAttribute, notUndefinedNull, sortElement, trimLowerCase } from '../helpers/shared';
import { useFormContext } from 'react-hook-form';
import { statusRequest } from '../constants/base';
import { useLocation } from 'react-router-dom';
import { getVisibleFormInputs } from '../helpers/base';
import { hiddenField, speedTier } from '../constants/form';

/**
 * Hook to get the data to shared/select
 * @param {String} name
 * @param {Function} setValue
 * @param {String} text
 * @param {Boolean} isStaticData
 * @param {Boolean} isSetValue
 * @returns
 */
export const useSelectSimple = (name, setValue, text, isStaticData, isSetValue) => {
  const { requestLanguage, allowedCountries } = useSelector(state => state.base);
  const { [`data_${trimLowerCase(requestLanguage)}`]: staticData } = useSelector(
    state => state.staticData);
  const { pathname } = useLocation()
  let options = isStaticData ? staticData[name] : name;
  options = isStaticData ? sortElement(options, text) : options;

  /* Only show allowedCountries, when accesing the user's Profile */
  if(pathname.includes('/user/profile') && name.includes('country')) options = allowedCountries

  const handleChange = ({ target }) => {
    (isSetValue) ? setValue(target.name, target.value) : setValue(target.value);
  };

  return { options, handleChange };
};

/**
 * Get element is disabled
 * @param {Object} field
 * @returns
 */
export const useDisabled = field => {
  const { action, code: tierCode } = useSelector(state => state.tier);
  const { dataProductRequest } = useSelector(state => state.user);
  const [isDisabled, setIsDisabled] = useState(false);
  const { infoProductRequest } = useSelector(state => state.user)
  const { code } = useSelector(state => state.base.country)
  const { isIBVRequired } = useSelector(state => state.base.ibv)
  const { setValue } = useFormContext()
  const { areDocumentsFetched, documentsFetched } = useSelector(state => state.base)
  const uniqueName = `${field?.unique_name}__id__${field?.id}`;
  const group = field?.unique_name?.split('__')[0]

  useEffect(() => {
    if (action === paramsAdd || action === paramsEdit) {
      const fieldAction = (field?.action) ? JSON.parse(field.action) : {};
      if (fieldAction?.attributes) {
        const index = Array.from(fieldAction.attributes).findIndex(
          item => item.name === 'disabled');
        setIsDisabled(index >= 0);
      }
    }
  }, [action, field]);

  useEffect(() => {
    const uniqueName = `${field?.unique_name}__id__${field?.id}`

    if (action === paramsAdd && uniqueName.startsWith(bankReferencesField) && infoProductRequest?.application?.unique_name === assistantsApp) {
      setValue(uniqueName, sOption)
    }

    // Adding condition for the 'user__american_citizen' field in the Check component
    if (uniqueName?.startsWith(userAmericanCitizenField)) {
      setTimeout(() => {
        if (action === paramsAdd && trimLowerCase(code) !== trimLowerCase(unitedStates)) {
          setValue(uniqueName, false);
          // getting into changeCitizenByOperator
          setTimeout(() => {
            changeCitizenByOperator(setValue, code);
          }, 50);
        } else if (action === paramsAdd && trimLowerCase(code) === trimLowerCase(unitedStates)) {
          setValue(uniqueName, true)
        }
      }, 500);
    }

    if (uniqueName?.startsWith(userAmericanCitizenField) && (isIBVRequired || tierCode === speedTier)) {
      const userAmericanCitizenCheckField = fieldAttribute(
        'name', uniqueName)
      let documentOperator = fieldAttribute(
          'data-unique-name', documentOperatorField);
      let documentOperatorValue = 'US';

      // * Logic for the '_sform' tier.
      if (!documentOperator && uniqueName?.includes('_sform') && action === paramsEdit){ 
        documentOperator = fieldAttribute('data-unique-name', `${documentOperatorField}_sform`);
        const documentNationality = fieldAttribute(
          'data-unique-name', `${documentNationalityField}_sform`);
        const documentLastNationality = fieldAttribute(
          'data-unique-name', `${documentLastNationalityField}_sform`);
        const documentResidence = fieldAttribute(
          'data-unique-name', `${documentResidenceField}_sform`);
        
        documentOperatorValue = (notUndefinedNull(documentOperator.value) && documentOperator.value !== hiddenField) ? documentOperator.value : 'US';
        setValue(documentNationality?.name, documentOperatorValue);
        setValue(documentLastNationality?.name,documentOperatorValue);
        setValue(documentResidence?.name,documentOperatorValue);
        
      }
      setValue(documentOperator?.name,documentOperatorValue);

      if (isIBVRequired) {
        setTimeout(() => {
          setValue(uniqueName, true);
        }, 700);
      }
      if (trimLowerCase(envSetting.isBlockedSelectNationality) === 'true') {
          const userCitizenshipCountry = fieldAttribute(
            'data-unique-name', userCitizenshipCountryField);

          if (userAmericanCitizenCheckField){
            userAmericanCitizenCheckField.setAttribute('disabled', '');
            userAmericanCitizenCheckField.setAttribute('readonly', '');
          }
          if (userCitizenshipCountry) {
            userCitizenshipCountry.setAttribute('disabled', '');
            userCitizenshipCountry.setAttribute('readonly', '');
            const citizenshipGroup = userCitizenshipCountry.closest(
              'div[id^="group_field_"]');
            if (citizenshipGroup) citizenshipGroup.classList.add('d-none');
          }
      }
    }

  }, [field, infoProductRequest?.application?.uniqueName])

  useEffect(() => {
    // TODO: Commented if this validation is needed on future.
    // const allDataUpdateRequests = Array.from(dataProductRequest).filter(
    //   item => item.application_origin.unique_name === appsOrigin.data_update);

    const currentlyUpdating = dataProductRequest && Array.from(dataProductRequest).some(request => request.status.unique_description === statusRequest.processing)

    if (action === paramsEdit) {
      if (field?.never_editable) {
        setIsDisabled(true);
      } else {
        setIsDisabled(true);
        if (currentlyUpdating) {
          setIsDisabled(false);
        }
      }
    }
  }, [action, field, dataProductRequest]);


  // ? Disabling relateds descriptions until `document-conditions` endpoint response is received
  const [showLoadingInput, setShowLoadingInput] = useState(false)
  useEffect(() => {
    if (documentsFetched?.includes(group) && uniqueName?.includes('description') ) {
      setShowLoadingInput(true)
    }
    setTimeout(() => {
      if (documentsFetched?.includes(group) && areDocumentsFetched) {
        setShowLoadingInput(false)
      }
    }, 2000)
  }, [areDocumentsFetched, documentsFetched])

  return { isDisabled, showLoadingInput };
};

/**
 * Get description_languague and language
 * @returns
 */
export const useTextLanguage = () => {
  const { actualLanguage } = useSelector(state => state.base);
  const languageText = `_${trimLowerCase(actualLanguage)}`;
  const description = `description${languageText}`;

  return { description, languageText };
};

/**
 * Hook to errors
 * @param {String} typeMessage
 * @returns
 */
export const useErrors = typeMessage => {
  const { t } = useTranslation();
  let message = '';

  switch (typeMessage) {
    case 'fieldRequired':
      message = t("common.fieldRequired");
      break;

    case 'fieldMin6':
      message = t("common.fieldMin", { min: 6 });
      break;

    case 'fieldMin8':
      message = t("common.fieldMin", { min: 8 });
      break;

    case 'emailInvalid':
      message = t("common.emailInvalid");
      break;

    case 'usernameRequired':
      message = t("getStarted.usernameRequired", { min: 6 });
      break;

    case 'passwordLowerCase':
      message = t("getStarted.passwordLowerCase");
      break;

    case 'passwordUpperCase':
      message = t("getStarted.passwordUpperCase");
      break;

    case 'passwordSpecial':
      message = t("getStarted.passwordSpecial", { characters });
      break;

    case 'passwordNumber':
      message = t("getStarted.passwordNumber");
      break;

    case 'passwordNotTheSame':
      message = t("password.notTheSame");
      break;

    case 'tokenFieldMax':
      message = t("common.fieldMax", { max: envSetting.maxMinutesToken });
      break;

    case 'phoneFieldMax':
      message = t("common.fieldMax", { max: '25' });
      break;

    case 'urlInvalid':
      message = t("common.urlInvalid");
      break;

    case 'integer':
      message = t("common.integer");
      break;

    case 'fieldMax6':
      message = t("common.fieldMax", { max: 6 });
      break;

    case 'documentNumberFieldMax':
      message = t("common.fieldMax", { max: '20' });
      break;

    default:
      const number = typeMessage.split('__').pop();
      message = typeMessage.split('__').shift();

      if (message === 'fieldMax') {
        message = t("common.fieldMax", { max: number });
      } else {
        if (message === 'fieldMin') {
          message = t("common.fieldMin", { min: number });
        } else if (message === 'fieldMinNumber') {
          message = t("common.fieldMinNumber", { min: number });
        } else {
          message = typeMessage;
        }
      }
      break;
  }

  return message;
};


/** 
* Hook to fix the 'hidden_input' being displayed on fields that are required on WE 
* but were not required previously, upon accour cretion (case RRHH, per example)
* @param {Object} methods - Methods object from ReactHookForm
* @returns
*/
export const useDetectAndFixVisibleHiddenInputs = (methods) => {
    const { action } = useSelector(state => state.tier);


    // ? This useEffect should fix the 'hidden_input' being displayed on fields that are required on WE 
    // ? but were not required previously, upon accour cretion (case RRHH, per example)
    useEffect(() => {
      // * Observer to watch for form changes in the DOM and load models when the relevant
      // * input (file__video) is detected. 
      if (action === paramsEdit) {
        const detectAndFixHiddenValueInputs = () => {
          const form = document.getElementsByTagName('form')[0]
          if (!form) return false
    
          const visibleInputs = getVisibleFormInputs(form)
          const hiddenValueInputs = [...visibleInputs]?.filter(input => input?.value === hiddenField)
    
          for (let i = 0; i < hiddenValueInputs.length; i++) {
            const element = hiddenValueInputs[i];
            element.value = ''
            if (!element?.name?.includes('block')) methods.setValue(element?.name, '')
          }
    
          return hiddenValueInputs.length > 0
        }
    
        const observer = new MutationObserver(() => {
          if (detectAndFixHiddenValueInputs()) {
            observer.disconnect() // Stop observing once the fields are detected and fixed
          }
        })
    
        if (detectAndFixHiddenValueInputs()) {
          return () => observer.disconnect()
        }
    
        observer.observe(document.body, { childList: true, subtree: true })
    
        return () => {
            observer.disconnect() // Cleanup observer on component unmount
        }
      }
    }, [action])

    return {}

}